import type { Tier } from "./config.js";

/**
 * Calculation module (brief section 9, ADR-006). This is the only implementation of the value maths.
 *
 * Every function is a direct port of the matching function in the reference tool, including its
 * empty-value handling, defaults and the order of floating point operations, so results are
 * identical to the reference to the last bit. The parity test in `test/parity.test.ts` checks this
 * against the reference HTML itself. Do not "tidy" an expression here without re-running it.
 *
 * Inputs are typed loosely (`unknown`) because the reference accepts numbers, numeric strings, ""
 * and missing values, and these functions must behave the same for raw imports and canonical maps.
 */

export type Which = "exp" | "low" | "high";

/** A block as the calculations see it: any object with a `type`. */
export type BlockLike = { type?: unknown } & Record<string, unknown>;

export interface GoalLike {
  vcase?: { blocks?: readonly BlockLike[] } | null;
  complexity?: ComplexityLike | null;
}

export interface ComplexityLike {
  scale?: unknown;
  skills?: unknown;
  dependencies?: unknown;
  change?: unknown;
  custom?: readonly { value?: unknown }[] | null;
}

/** parseFloat, with anything that is not a finite number treated as 0. */
export function num0(v: unknown): number {
  const x = parseFloat(String(v));
  return isFinite(x) ? x : 0;
}

/** parseFloat, with anything that is not a finite number replaced by `fallback`. */
export function pick(a: unknown, fallback: number): number {
  const x = parseFloat(String(a));
  return isFinite(x) ? x : fallback;
}

export function sumArr(a: readonly number[]): number {
  return a.reduce((s, x) => s + x, 0);
}

export function addSeries(a: readonly number[], b: readonly number[]): number[] {
  const r: number[] = [];
  const n = Math.max(a.length, b.length);
  for (let i = 0; i < n; i++) r.push((a[i] || 0) + (b[i] || 0));
  return r;
}

/** Yearly values for one block over `years` (default 3) for the expected, low or high case. */
export function blockSeries(b: BlockLike, years?: number, which?: Which): number[] {
  years = years || 3;
  const arr: number[] = [];
  let y: number;
  if (b.type === "revenue") {
    let base: number, annual: number;
    if (b.method === "drivers") {
      base = num0(b.donors) * num0(b.avgGift);
      const newRev =
        (num0(b.donors) + num0(b.extraDonors)) *
        (num0(b.avgGift) * (1 + num0(b.avgGiftIncreasePct) / 100));
      annual = newRev - base;
    } else {
      base = num0(b.baseline);
      const pct =
        which === "low"
          ? pick(b.upliftPctLow, num0(b.upliftPct))
          : which === "high"
            ? pick(b.upliftPctHigh, num0(b.upliftPct))
            : num0(b.upliftPct);
      annual = (base * num0(pct)) / 100;
    }
    const ramp = b.ramp == null ? 100 : num0(b.ramp);
    const g = num0(b.growthPct) / 100;
    for (y = 1; y <= years; y++)
      arr.push(annual * Math.pow(1 + g, y - 1) * (y === 1 ? ramp / 100 : 1));
  } else if (b.type === "efficiency") {
    const hrsWk =
      num0(b.hoursPerWeekEach) *
      num0(b.staff) *
      ((b.savingPct == null ? 100 : num0(b.savingPct)) / 100);
    const wks = b.weeksPerYear == null ? 48 : num0(b.weeksPerYear);
    const perYear = hrsWk * wks * num0(b.hourlyCost);
    const g2 = num0(b.growthPct) / 100;
    for (y = 1; y <= years; y++) arr.push(perYear * Math.pow(1 + g2, y - 1));
  } else if (b.type === "cost") {
    const amt =
      num0(b.annualAmount) * ((b.likelihoodPct == null ? 100 : num0(b.likelihoodPct)) / 100);
    const g3 = num0(b.growthPct) / 100;
    for (y = 1; y <= years; y++) arr.push(amt * Math.pow(1 + g3, y - 1));
  }
  return arr;
}

function blocksOf(g: GoalLike): readonly BlockLike[] {
  return (g.vcase && g.vcase.blocks) || [];
}

/** Sum of a goal's block series, block by block, as the reference does. */
export function goalSeries(g: GoalLike, years: number, which: Which = "exp"): number[] {
  let t: number[] = [];
  blocksOf(g).forEach((b) => {
    t = addSeries(t, blockSeries(b, years, which));
  });
  return t;
}

/** Reference `goalCumValue`: cumulative expected value over the horizon. */
export function goalCumValue(g: GoalLike, years: number): number {
  return sumArr(goalSeries(g, years, "exp"));
}

/** Reference `goalY1Value`: expected value in year 1. */
export function goalY1Value(g: GoalLike, years: number): number {
  return goalSeries(g, years, "exp")[0] || 0;
}

/** Mean of the complexity factors, where an unset (falsy) factor counts as 3. */
export function complexityScore(g: GoalLike): number {
  const c: ComplexityLike = g.complexity || {};
  const vals = [
    num0(c.scale || 3),
    num0(c.skills || 3),
    num0(c.dependencies || 3),
    num0(c.change || 3),
  ];
  (c.custom || []).forEach((f) => {
    vals.push(num0(f.value || 3));
  });
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

/** Value at or above half the highest goal value is high; complexity 3 or more is high. */
export function tierOf(value: number, cx: number, maxValue: number): Tier {
  const vHigh = maxValue > 0 && value >= 0.5 * maxValue;
  const cHigh = cx >= 3;
  if (vHigh && !cHigh) return { key: "quick", label: "Quick win" };
  if (vHigh && cHigh) return { key: "bet", label: "Big bet" };
  if (!vHigh && !cHigh) return { key: "fill", label: "Fill-in" };
  return { key: "recon", label: "Reconsider" };
}

export interface GoalPriority {
  /** Position of the goal in the map (0 based); the reference shows `index + 1`. */
  index: number;
  goalId: string;
  value: number;
  y1: number;
  cx: number;
  tier: Tier;
  priority: number;
}

export interface Prioritisation {
  maxValue: number;
  /** In map order. */
  goals: GoalPriority[];
  /** In priority order (reference `renderRank`). */
  ranked: GoalPriority[];
}

/** Reference `updatePrioritise` and `renderRank`. */
export function prioritise(
  goals: readonly (GoalLike & { id: string })[],
  years: number,
): Prioritisation {
  const data = goals.map((g, i) => ({
    index: i,
    goalId: g.id,
    value: goalCumValue(g, years),
    y1: goalY1Value(g, years),
    cx: complexityScore(g),
  }));
  const maxValue = Math.max(...data.map((d) => d.value).concat([0]));
  const withTier: GoalPriority[] = data.map((d) => ({
    ...d,
    tier: tierOf(d.value, d.cx, maxValue),
    priority: d.cx > 0 ? d.value / d.cx : 0,
  }));
  const ranked = withTier.slice().sort((a, b) => {
    if (maxValue <= 0) return a.cx - b.cx;
    return b.priority - a.priority;
  });
  return { maxValue, goals: withTier, ranked };
}

export interface MapTotals {
  /** Expected value per year, summed goal by goal. */
  series: number[];
  year1: number;
  cumulativeExpected: number;
  cumulativeLow: number;
  cumulativeHigh: number;
  /** Hours freed per year across all efficiency blocks. */
  hoursPerYear: number;
}

/**
 * Reference `updateQuantify`. Expected totals are summed goal by goal while low and high totals are
 * summed block by block across the whole map, exactly as the reference does.
 */
export function mapTotals(goals: readonly GoalLike[], years: number): MapTotals {
  let totalExp: number[] = [];
  let totalLow: number[] = [];
  let totalHigh: number[] = [];
  let hoursYr = 0;
  goals.forEach((g) => {
    let ge: number[] = [];
    blocksOf(g).forEach((b) => {
      ge = addSeries(ge, blockSeries(b, years, "exp"));
      totalLow = addSeries(totalLow, blockSeries(b, years, "low"));
      totalHigh = addSeries(totalHigh, blockSeries(b, years, "high"));
      if (b.type === "efficiency") {
        const wks = b.weeksPerYear == null ? 48 : num0(b.weeksPerYear);
        hoursYr +=
          num0(b.hoursPerWeekEach) *
          num0(b.staff) *
          ((b.savingPct == null ? 100 : num0(b.savingPct)) / 100) *
          wks;
      }
    });
    totalExp = addSeries(totalExp, ge);
  });
  return {
    series: totalExp,
    year1: totalExp[0] || 0,
    cumulativeExpected: sumArr(totalExp),
    cumulativeLow: sumArr(totalLow),
    cumulativeHigh: sumArr(totalHigh),
    hoursPerYear: hoursYr,
  };
}

export type BlockOutputs =
  | {
      type: "revenue";
      baseline: number;
      /** Only meaningful for the drivers method; the reference shows it to 1 decimal place. */
      impliedUpliftPct: number;
      year1: number;
      recurring: number;
      total: number;
      low: number;
      high: number;
    }
  | {
      type: "efficiency";
      hoursPerWeek: number;
      hoursPerYear: number;
      fte: number;
      valuePerYear: number;
      total: number;
    }
  | { type: "cost"; perYear: number; total: number }
  | { type: "unknown" };

/** The figures shown under each block in the Quantify view (reference `updateBlockOut`). */
export function blockOutputs(b: BlockLike, years: number): BlockOutputs {
  const exp = blockSeries(b, years, "exp");
  const cum = sumArr(exp);
  if (b.type === "revenue") {
    const base = b.method === "drivers" ? num0(b.donors) * num0(b.avgGift) : num0(b.baseline);
    let annualFull: number;
    if (b.method === "drivers")
      annualFull =
        (num0(b.donors) + num0(b.extraDonors)) *
          (num0(b.avgGift) * (1 + num0(b.avgGiftIncreasePct) / 100)) -
        base;
    else annualFull = (base * num0(b.upliftPct)) / 100;
    const implied = base > 0 ? (annualFull / base) * 100 : 0;
    return {
      type: "revenue",
      baseline: base,
      impliedUpliftPct: implied,
      year1: exp[0] || 0,
      recurring: exp[years - 1] || 0,
      total: cum,
      low: sumArr(blockSeries(b, years, "low")),
      high: sumArr(blockSeries(b, years, "high")),
    };
  }
  if (b.type === "efficiency") {
    const hrsWk =
      num0(b.hoursPerWeekEach) *
      num0(b.staff) *
      ((b.savingPct == null ? 100 : num0(b.savingPct)) / 100);
    const wks = b.weeksPerYear == null ? 48 : num0(b.weeksPerYear);
    const hrsYr = hrsWk * wks;
    return {
      type: "efficiency",
      hoursPerWeek: hrsWk,
      hoursPerYear: hrsYr,
      fte: hrsWk / 38,
      valuePerYear: hrsYr * num0(b.hourlyCost),
      total: cum,
    };
  }
  if (b.type === "cost") return { type: "cost", perYear: exp[0] || 0, total: cum };
  return { type: "unknown" };
}
